import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData } from '../../services/request'

const AdminJobApprovals = () => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionJobId, setActionJobId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  const fetchJobs = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/jobs/pending`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setJobs(Array.isArray(data) ? data : [])
      } else {
        setJobs([])
        setError(res.result?.data?.message || res.result?.data?.data || 'Unable to load jobs')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [])

  const handleApprove = async (jobId) => {
    setActionJobId(jobId)
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: 'LIVE' })
    setActionJobId(null)
    if (res.error === '' && res.result?.status === 200) {
      setJobs((prev) => prev.filter((j) => j._id !== jobId))
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to approve')
    }
  }

  const openRejectModal = (jobId) => {
    setActionJobId(jobId)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) return
    const jobId = actionJobId
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: 'REJECTED', reason: rejectReason.trim() })
    setActionJobId(null)
    setShowRejectModal(false)
    setRejectReason('')
    if (res.error === '' && res.result?.status === 200) {
      setJobs((prev) => prev.filter((j) => j._id !== jobId))
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to reject')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const formatCurrency = (amount) => {
    if (amount == null || amount === undefined) return '—'
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount)
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Approvals</h1>
          <p className="text-sm text-gray-600">
            Approve or reject job listings that have been paid for and are pending admin approval
          </p>
        </div>
        <Link
          to="/admin/app/employme"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to overview
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No jobs pending approval</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Job Title</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Payment</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Submitted</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {jobs.map((job) => (
                  <tr key={job._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/app/employme/job-approvals/${job._id}`} className="font-medium text-gray-900 hover:underline">
                        {job.title}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {job.employmentType || '—'} • {job.locationType || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {job.companyId?.name || job.companyId?.contactPersonName || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(job.paymentAmount)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(job.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/admin/app/employme/job-approvals/${job._id}`}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          View
                        </Link>
                        <button
                          onClick={() => handleApprove(job._id)}
                          disabled={actionJobId === job._id}
                          className="rounded-lg border border-green-600 bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {actionJobId === job._id ? 'Approving...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => openRejectModal(job._id)}
                          disabled={actionJobId === job._id}
                          className="rounded-lg border border-red-600 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Reject job listing</h3>
            <p className="mb-3 text-sm text-gray-600">Please provide a reason for rejection (required):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Job description does not meet platform guidelines..."
              rows={4}
              className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setActionJobId(null)
                  setRejectReason('')
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default AdminJobApprovals
